import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Check, Clock, MessageCircle, Loader2, FileText, LogOut, Stethoscope, AlertTriangle,
  Wallet, Download, FileSpreadsheet, FileText as FileTextIcon
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import NotificationBell from "../../components/NotificationBell";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const PrescriptionsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  const [tab, setTab] = useState("prescriptions");
  const [commissions, setCommissions] = useState([]);
  const [commissionTotals, setCommissionTotals] = useState(null);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const isPending = user?.status === "pending";

  const STATUS = {
    pending_doctor: { label: t("doctor.statusPendingDoctor"), className: "bg-amber-100 text-amber-700" },
    on_hold: { label: t("doctor.statusOnHold"), className: "bg-blue-100 text-blue-700" },
    validated: { label: t("doctor.statusValidated"), className: "bg-green-100 text-green-700" },
    rejected: { label: t("doctor.statusRejected"), className: "bg-red-100 text-red-700" },
    unmatched: { label: t("doctor.statusUnmatched"), className: "bg-slate-100 text-slate-600" },
  };

  const load = async () => {
    try {
      const res = await apiClient.get("/prescriptions");
      setItems(res.data || []);
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadCommissions = async () => {
    setCommissionsLoading(true);
    try {
      const res = await apiClient.get("/doctor/commissions");
      setCommissions(res.data.commissions || []);
      setCommissionTotals(res.data.totals || null);
    } catch (error) {
      toastApiError(error);
    } finally {
      setCommissionsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "commissions") loadCommissions();
  }, [tab]);

  const downloadCommissionExport = async (format) => {
    try {
      const res = await apiClient.get(`/doctor/commissions/export.${format}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mes-commissions-livraison.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastApiError(error);
    }
  };

  const openDetail = async (item) => {
    setSelected(item);
    setNote(item.note || "");
    try {
      const res = await apiClient.get(`/prescriptions/${item.id}`);
      setDetail(res.data);
    } catch (error) {
      toastApiError(error);
      setDetail(item);
    }
  };

  const act = async (action, item = selected) => {
    if (!item) return;
    setActing(true);
    try {
      const res = await apiClient.put(`/prescriptions/${item.id}/${action}`, { note });
      toast.success(res.data.message || t("doctor.actionRecorded"));
      if (action === "hold" && res.data.conversation_id) {
        setSelected(null);
        navigate(`/messages/${res.data.conversation_id}`);
        return;
      }
      setSelected(null);
      load();
    } catch (error) {
      toastApiError(error);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="OMU" className="h-10 w-auto" />
            <span className="font-heading font-semibold text-slate-800 hidden sm:inline">{t("doctor.title")}</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <NotificationBell />
            <Button variant="ghost" onClick={() => navigate("/messages")}>
              <MessageCircle className="h-4 w-4 mr-2" /> {t("doctor.chat")}
            </Button>
            <Button variant="outline" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {user?.status === "pending" && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {t("doctor.pendingAlert")}
            </AlertDescription>
          </Alert>
        )}
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Stethoscope className="h-6 w-6 text-omu-blue-500" />
            {t("doctor.pageTitle")}
          </h1>
          <p className="text-slate-500 mt-1">
            {t("doctor.pageSubtitle", { cnom: user?.numero_ordre || "—" })}
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="prescriptions"><FileText className="h-4 w-4 mr-1" />{t("doctor.tabPrescriptions")}</TabsTrigger>
            <TabsTrigger value="commissions"><Wallet className="h-4 w-4 mr-1" />{t("doctor.tabCommissions")}</TabsTrigger>
          </TabsList>

          <TabsContent value="commissions" className="mt-4">
            {!commissionsLoading && commissionTotals && (
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                <span className="rounded-xl bg-amber-50 text-amber-800 px-4 py-2">
                  {t("doctor.totalDue")} : <strong>{Number(commissionTotals.due_total || 0).toLocaleString()} CDF</strong>
                </span>
                <span className="rounded-xl bg-green-50 text-green-800 px-4 py-2">
                  {t("doctor.totalPaid")} : <strong>{Number(commissionTotals.paid_total || 0).toLocaleString()} CDF</strong>
                </span>
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" size="sm" onClick={() => downloadCommissionExport("csv")}>
                    <Download className="h-4 w-4 mr-1" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadCommissionExport("xlsx")}>
                    <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadCommissionExport("pdf")}>
                    <FileTextIcon className="h-4 w-4 mr-1" /> PDF
                  </Button>
                </div>
              </div>
            )}
            {commissionsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" /></div>
            ) : commissions.length === 0 ? (
              <Card className="border-0 shadow-soft">
                <CardContent className="p-10 text-center text-slate-500">
                  <Wallet className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  {t("doctor.noCommissions")}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {commissions.map((c) => (
                  <Card key={c.id} className="border-0 shadow-soft">
                    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <p className="font-medium">{c.pharmacy_name} — {c.medicine_name}</p>
                        <p className="text-sm text-slate-500">
                          {t("doctor.deliveryFee", {
                            amount: Number(c.delivery_fee || 0).toLocaleString(),
                            currency: c.currency || "CDF",
                          })}
                          {c.delivery_zone_name ? ` · ${c.delivery_zone_name}` : ""}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-omu-blue-600">
                          {Number(c.commission_amount || 0).toLocaleString()} {c.currency || "CDF"}
                        </p>
                        <p className="text-xs text-slate-500">{t("doctor.commissionRateOfDelivery", { rate: c.commission_rate })}</p>
                        <Badge className={c.status === "paid" ? "bg-green-100 text-green-800 mt-1" : "bg-amber-100 text-amber-800 mt-1"}>
                          {c.status === "paid" ? t("doctor.statusPaid") : t("doctor.statusDue")}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="prescriptions" className="mt-4">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" /></div>
        ) : items.length === 0 ? (
          <Card className="border-0 shadow-soft">
            <CardContent className="p-10 text-center text-slate-500">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
              {t("doctor.noPrescriptions")}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const st = STATUS[item.status] || STATUS.pending_doctor;
              return (
                <Card key={item.id} className="border-0 shadow-soft hover:shadow-md transition">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => openDetail(item)}>
                      <div>
                        <p className="font-medium text-slate-900">{item.client_name || t("doctor.patient")}</p>
                        <p className="text-sm text-slate-500">
                          {(item.medicines || []).map((m) => m.detected?.name || m.matched?.name || m.name).filter(Boolean).join(", ") || t("doctor.medicinesNotListed")}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {item.created_at ? new Date(item.created_at).toLocaleString() : ""}
                        </p>
                      </div>
                      <Badge className={st.className}>{st.label}</Badge>
                    </div>
                    {item.status === "pending_doctor" && !isPending && (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 rounded-full" disabled={acting} onClick={(e) => { e.stopPropagation(); act("validate", item); }}>
                          <Check className="h-4 w-4 mr-1" /> {t("doctor.validate")}
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full" disabled={acting} onClick={(e) => { e.stopPropagation(); act("hold", item); }}>
                          <Clock className="h-4 w-4 mr-1" /> {t("doctor.holdAndChat")}
                        </Button>
                      </div>
                    )}
                    {item.status === "on_hold" && (
                      <Button size="sm" className="rounded-full bg-omu-blue-500 hover:bg-omu-blue-600" onClick={(e) => {
                        e.stopPropagation();
                        if (item.conversation_id) navigate(`/messages/${item.conversation_id}`);
                        else { setSelected(item); act("hold", item); }
                      }}>
                        <MessageCircle className="h-4 w-4 mr-1" /> {t("doctor.openChat")}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("doctor.prescriptionDialogTitle", { name: selected?.client_name })}</DialogTitle>
          </DialogHeader>
          {detail?.image_data && (
            <img src={detail.image_data.startsWith("data:") ? detail.image_data : `data:image/jpeg;base64,${detail.image_data}`} alt="Ordonnance" className="w-full rounded-xl border" />
          )}
          <div className="text-sm text-slate-600 space-y-1">
            <p>{t("doctor.cnom")} : <strong>{detail?.cnom || selected?.cnom || "—"}</strong></p>
            <p>{t("doctor.detectedDoctor")} : {detail?.doctor_name_detected || "—"}</p>
            <ul className="list-disc pl-5">
              {(detail?.medicines || selected?.medicines || []).map((m, i) => (
                <li key={i}>{m.detected?.name || m.matched?.name || m.name || t("doctor.medicine")}</li>
              ))}
            </ul>
          </div>
          {selected && selected.status === "pending_doctor" && !isPending && (
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button className="bg-green-600 hover:bg-green-700" disabled={acting} onClick={() => act("validate")}>
                <Check className="h-4 w-4 mr-1" /> {t("doctor.validate")}
              </Button>
              <Button variant="outline" disabled={acting} onClick={() => act("hold")}>
                <Clock className="h-4 w-4 mr-1" /> {t("doctor.holdAndChat")}
              </Button>
            </DialogFooter>
          )}
          {selected && selected.status === "on_hold" && (
            <DialogFooter>
              <Button className="bg-omu-blue-500 hover:bg-omu-blue-600" onClick={() => {
                const convId = detail?.conversation_id || selected.conversation_id;
                setSelected(null);
                if (convId) navigate(`/messages/${convId}`);
                else act("hold");
              }}>
                <MessageCircle className="h-4 w-4 mr-1" /> {t("doctor.openChatValidateReject")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrescriptionsPage;
